import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Calendar, 
  Tag, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  BarChart2, 
  Target,
  Edit3,
  Trash2,
  PlusCircle,
  X,
  PlayCircle,
  Info
} from 'react-feather';
import { releasesApi, featureTagApi, projectApi, taskApi } from '../services/api';
import { getProgressColor } from '../utils/projectUtils';
import { validateReleaseCompletion, createStatusChangeHandler, getNextStatus, getStatusChangeButtonLabel, shouldShowStatusChangeButton, isReleaseReadyForRelease } from '../utils/releaseUtils';
import ReadyForReleaseBadge from '../components/Releases/ReadyForReleaseBadge';
import FeatureTagDetailModal from '../components/FeatureTags/FeatureTagDetailModal';
import './ReleaseDetail.css';

const ReleaseDetail = () => {
  const { projectId, releaseId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [release, setRelease] = useState(null);
  const [featureTags, setFeatureTags] = useState([]);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRelease, setEditingRelease] = useState(null);
  
  // UI state
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [selectedFeatureTags, setSelectedFeatureTags] = useState([]);
  const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [projectId, releaseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [releaseData, featureTagsData, projectData, tasksData] = await Promise.all([
        releasesApi.getRelease(releaseId),
        featureTagApi.getByProject(projectId),
        projectApi.get(projectId),
        taskApi.getByProject(projectId)
      ]);
      
      setRelease(releaseData);
      setFeatureTags(featureTagsData);
      setProject(projectData);
      setTasks(tasksData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load release data');
    } finally {
      setLoading(false);
    }
  };

  // Handle update release
  const handleUpdateRelease = async (e) => {
    e.preventDefault();
    
    // Validate that all tasks are completed before marking release as completed
    const validation = validateReleaseCompletion(release, tasks, editingRelease.status);
    if (!validation.isValid) {
      setError(validation.errorMessage);
      return;
    }
    
    try {
      setError(null);
      const updatedRelease = await releasesApi.updateRelease(editingRelease.id, editingRelease);
      setRelease(updatedRelease);
      setEditingRelease(null);
    } catch (err) {
      console.error('Error updating release:', err);
      // Check if it's a validation error from the backend
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to update release');
      }
    }
  };

  // Handle quick status change
  const handleQuickStatusChange = createStatusChangeHandler(
    release,
    tasks,
    (updatedRelease) => {
      setRelease(updatedRelease);
      setError(null);
    },
    (errorMessage) => {
      setError(errorMessage);
    }
  );

  // Handle delete release
  const handleDeleteRelease = async () => {
    if (window.confirm('Are you sure you want to delete this release?')) {
      try {
        await releasesApi.deleteRelease(releaseId);
        navigate(`/projects/${projectId}/releases`);
      } catch (err) {
        console.error('Error deleting release:', err);
        setError('Failed to delete release');
      }
    }
  };

  // Handle add feature tags to release
  const handleAddFeatureTags = async () => {
    try {
      const promises = selectedFeatureTags.map(featureTagId =>
        releasesApi.addFeatureTag(releaseId, featureTagId)
      );
      
      await Promise.all(promises);
      await loadData(); // Reload to get updated data
      setShowAddFeatureModal(false);
      setSelectedFeatureTags([]);
    } catch (err) {
      console.error('Error adding feature tags:', err);
      setError('Failed to add feature tags');
    }
  };

  // Handle remove feature tag from release
  const handleRemoveFeatureTag = async (featureTagId) => {
    try {
      await releasesApi.removeFeatureTag(releaseId, featureTagId);
      await loadData(); // Reload to get updated data
    } catch (err) {
      console.error('Error removing feature tag:', err);
      setError('Failed to remove feature tag');
    }
  };

  // Get available feature tags (not already in the release)
  const getAvailableFeatureTags = () => {
    const releaseFeatureTagIds = release?.release_feature_tags?.map(rft => rft.feature_tag.id) || [];
    return featureTags.filter(tag => !releaseFeatureTagIds.includes(tag.id));
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#059669';
      case 'in_progress': return '#2563eb';
      case 'planned': return '#4b5563';
      case 'cancelled': return '#dc2626';
      default: return '#4b5563';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} />;
      case 'in_progress': return <Clock size={16} />;
      case 'planned': return <Target size={16} />;
      case 'cancelled': return <X size={16} />;
      default: return <Target size={16} />;
    }
  };

  // Get feature tag progress data from insights
  const getFeatureTagProgress = (featureTagId) => {
    if (!release?.insights?.feature_tags) return null;
    return release.insights.feature_tags.find(ft => ft.id === featureTagId);
  };

  // Calculate resource impact for a feature (pending tasks by user and unassigned tasks)
  const getFeatureResourceImpact = (featureTagId) => {
    // Get all tasks for this feature tag that are not completed
    const featureTasks = tasks.filter(task => 
      task.feature_tag?.id === featureTagId && 
      task.status !== 'completed'
    );

    // Group by assigned user
    const tasksByUser = {};
    let unassignedCount = 0;

    featureTasks.forEach(task => {
      if (task.assigned_to?.id) {
        const userId = task.assigned_to.id;
        const userName = task.assigned_to.display_name || 'Unknown';
        if (!tasksByUser[userId]) {
          tasksByUser[userId] = {
            name: userName,
            count: 0
          };
        }
        tasksByUser[userId].count++;
      } else {
        unassignedCount++;
      }
    });

    return {
      tasksByUser: Object.values(tasksByUser),
      unassignedCount
    };
  };

  if (loading) {
    return (
      <div className="release-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading release details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="release-detail">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button 
            className="retry-button"
            onClick={() => {
              setError(null);
              loadData();
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="release-detail">
        <div className="error-container">
          <h3>Release Not Found</h3>
          <p>The release you're looking for doesn't exist.</p>
          <button 
            className="retry-button"
            onClick={() => navigate(`/projects/${projectId}/releases`)}
          >
            Back to Releases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="release-detail">
      {/* Header */}
      <div className="release-detail__header">
        <div className="release-detail__header-left">
          <button 
            className="back-button"
            onClick={() => navigate(`/projects/${projectId}/releases`)}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1>{release.name}{project?.name ? ` - ${project.name}` : ''}</h1>
              {isReleaseReadyForRelease(release, tasks) && <ReadyForReleaseBadge />}
            </div>
            <p>Version {release.version} • {release.description}</p>
          </div>
        </div>
        <div className="release-detail__header-actions">
          {shouldShowStatusChangeButton(release.status) && (
            <button 
              className="action-button primary"
              onClick={() => handleQuickStatusChange(getNextStatus(release.status))}
            >
              {release.status === 'planned' ? (
                <PlayCircle size={16} />
              ) : (
                <CheckCircle size={16} />
              )}
              <span>{getStatusChangeButtonLabel(release.status)}</span>
            </button>
          )}
          <button 
            className="action-button secondary"
            onClick={() => setEditingRelease(release)}
          >
            <Edit3 size={16} />
          </button>
          <button 
            className="action-button danger"
            onClick={handleDeleteRelease}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Release Information */}
      <div className="release-detail__info">
        <div className="info-section">
          <h3>Release Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span 
                className="info-value status"
                style={{ color: getStatusColor(release.status) }}
              >
                {getStatusIcon(release.status)}
                {release.status.replace('_', ' ')}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Target Date:</span>
              <span className="info-value">{formatDate(release.target_date)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created:</span>
              <span className="info-value">{formatDate(release.created_at)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created By:</span>
              <span className="info-value">{release.created_by?.display_name || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Release Insights */}
        {release.insights && (
          <div className="insights-section">
            <h3>Release Insights</h3>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-icon">
                  <BarChart2 size={16} />
                </div>
                <div className="insight-content">
                  <div className="insight-value">{release.insights.total_tasks || 0}</div>
                  <div className="insight-label">Total Tasks</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <CheckCircle size={16} />
                </div>
                <div className="insight-content">
                  <div className="insight-value">{release.insights.completed_tasks || 0}</div>
                  <div className="insight-label">Completed</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <Clock size={16} />
                </div>
                <div className="insight-content">
                  <div className="insight-value">{release.insights.in_progress_tasks || 0}</div>
                  <div className="insight-label">In Progress</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="insight-content">
                  <div className="insight-value">{release.insights.overdue_tasks || 0}</div>
                  <div className="insight-label">Overdue</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon hours">
                  <Clock size={16} />
                </div>
                <div className="insight-content">
                  <div className="insight-value">{(parseFloat(release.insights.total_hours) || 0).toFixed(1)}h</div>
                  <div className="insight-label">Total Hours</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Release Progress */}
        {release.insights && release.insights.completion_percentage !== undefined && (
          <div className="progress-section">
            <h3>Release Progress</h3>
            <div className="progress-header">
              <span className="progress-label">Overall Completion</span>
              <span className="progress-percentage">
                {release.insights.completion_percentage || 0}%
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${release.insights.completion_percentage || 0}%`,
                  backgroundColor: getProgressColor(release.insights.completion_percentage || 0)
                }} 
              />
            </div>
            <div className="progress-details">
              <span className="progress-text">
                {release.insights.completed_tasks || 0} of {release.insights.total_tasks || 0} tasks completed
              </span>
            </div>
          </div>
        )}

        {/* Feature Tags */}
        <div className="features-section">
          <div className="features-header">
            <h3>Feature Tags</h3>
            <button 
              className="add-features-button"
              onClick={() => setShowAddFeatureModal(true)}
            >
            <PlusCircle size={14} />
              Add Features
            </button>
          </div>
          
          {!release.release_feature_tags || release.release_feature_tags.length === 0 ? (
            <div className="no-features">
              <Tag size={48} />
              <p>No feature tags assigned to this release</p>
              <button 
                className="add-features-button"
                onClick={() => setShowAddFeatureModal(true)}
              >
            <PlusCircle size={14} />
                Add Features
              </button>
            </div>
          ) : (
            <div className="features-list">
              {[...release.release_feature_tags].sort((a, b) => {
                const progressA = getFeatureTagProgress(a.feature_tag.id)?.completion_percentage || 0;
                const progressB = getFeatureTagProgress(b.feature_tag.id)?.completion_percentage || 0;
                // Completed features (100%) go to the end
                if (progressA === 100 && progressB !== 100) return 1;
                if (progressA !== 100 && progressB === 100) return -1;
                return 0;
              }).map(rft => {
                const progressData = getFeatureTagProgress(rft.feature_tag.id);
                const completionPercentage = progressData?.completion_percentage || 0;
                const taskCount = progressData?.task_count || 0;
                const completedTasks = progressData?.completed_tasks || 0;
                const featureHours = parseFloat(progressData?.hours) || 0;
                const isIncomplete = completionPercentage < 100;
                const resourceImpact = isIncomplete ? getFeatureResourceImpact(rft.feature_tag.id) : null;
                
                return (
                <div key={rft.id} className="feature-item">
                  <div className="feature-main">
                    <div className="feature-info">
                      <div 
                        className="feature-color"
                        style={{ backgroundColor: rft.feature_tag.color }}
                      />
                      <div className="feature-details">
                        <div className="feature-details-row">
                          <span className="feature-name">{rft.feature_tag.name}</span>
                          <button
                            type="button"
                            className="feature-details-info-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFeatureTag(rft.feature_tag);
                            }}
                            title="View feature details"
                            aria-label="View feature details"
                          >
                            <Info size={14} />
                          </button>
                        </div>
                        {rft.feature_tag.due_date && (
                          <span className="feature-due-date">
                            Due: {formatDate(rft.feature_tag.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    {taskCount > 0 ? (
                      <div className="feature-progress">
                        <div className="feature-progress-header">
                          <span className="feature-progress-percentage">
                            {completionPercentage.toFixed(0)}%
                          </span>
                          <span className="feature-progress-tasks">
                            {completedTasks}/{taskCount} tasks
                          </span>
                          {featureHours > 0 && (
                            <span className="feature-progress-hours">
                              {featureHours.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        <div className="feature-progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ 
                              width: `${completionPercentage}%`,
                              backgroundColor: getProgressColor(completionPercentage)
                            }} 
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="feature-no-tasks">
                        <span>No tasks present</span>
                      </div>
                    )}
                    <button 
                      className="remove-feature-button"
                      onClick={() => handleRemoveFeatureTag(rft.feature_tag.id)}
                      title="Remove from release"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {isIncomplete && resourceImpact && (
                    resourceImpact.tasksByUser.length > 0 || resourceImpact.unassignedCount > 0 ? (
                      <div className="feature-resource-impact">
                        {resourceImpact.tasksByUser.length > 0 && (
                          <div className="resource-impact-section">
                            <span className="resource-impact-label">Resources:</span>
                            <div className="resource-impact-list">
                              {resourceImpact.tasksByUser.map((user, idx) => (
                                <span key={idx} className="resource-impact-item">
                                  {user.name} ({user.count})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {resourceImpact.unassignedCount > 0 && (
                          <div className="resource-impact-section">
                            <span className="resource-impact-label">Unassigned tasks:</span>
                            <span className="resource-impact-value">{resourceImpact.unassignedCount}</span>
                          </div>
                        )}
                      </div>
                    ) : null
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Release Modal */}
      {editingRelease && (
        <div className="modal-overlay" onClick={() => setEditingRelease(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>Edit Release</h2>
              <button 
                className="modal__close"
                onClick={() => setEditingRelease(null)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateRelease} className="modal__content">
              <div className="release-form-row">
                <div className="release-form-group">
                <label htmlFor="name">Release Name</label>
                <input
                  type="text"
                  id="name"
                  value={editingRelease.name}
                  onChange={(e) => setEditingRelease({...editingRelease, name: e.target.value})}
                  required
                />
              </div>
              
                <div className="release-form-group">
                <label htmlFor="version">Version</label>
                <input
                  type="text"
                  id="version"
                  value={editingRelease.version}
                  onChange={(e) => setEditingRelease({...editingRelease, version: e.target.value})}
                  required
                />
                </div>
              </div>
              
              <div className="release-form-group release-form-group-full">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={editingRelease.description}
                  onChange={(e) => setEditingRelease({...editingRelease, description: e.target.value})}
                  rows="6"
                />
              </div>
              
              <div className="release-form-row">
                <div className="release-form-group">
                <label htmlFor="target_date">Target Date</label>
                <input
                  type="date"
                  id="target_date"
                  value={editingRelease.target_date ? editingRelease.target_date.split('T')[0] : ''}
                  onChange={(e) => setEditingRelease({...editingRelease, target_date: e.target.value})}
                />
              </div>
              
                <div className="release-form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={editingRelease.status}
                  onChange={(e) => setEditingRelease({...editingRelease, status: e.target.value})}
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                </div>
              </div>
              
              <div className="modal__actions">
                <button type="button" onClick={() => setEditingRelease(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Update Release
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Feature Tags Modal */}
      {showAddFeatureModal && (
        <div className="modal-overlay" onClick={() => setShowAddFeatureModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>Add Feature Tags</h2>
              <button 
                className="modal__close"
                onClick={() => setShowAddFeatureModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal__content">
              <div className="feature-tags-selection">
                {getAvailableFeatureTags().map(tag => (
                  <label key={tag.id} className="feature-tag-option">
                    <input
                      type="checkbox"
                      checked={selectedFeatureTags.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFeatureTags([...selectedFeatureTags, tag.id]);
                        } else {
                          setSelectedFeatureTags(selectedFeatureTags.filter(id => id !== tag.id));
                        }
                      }}
                    />
                    <div className="feature-tag-preview">
                      <div 
                        className="feature-color"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span>{tag.name}</span>
                    </div>
                  </label>
                ))}
              </div>
              
              <div className="modal__actions">
                <button type="button" onClick={() => setShowAddFeatureModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="primary"
                  onClick={handleAddFeatureTags}
                  disabled={selectedFeatureTags.length === 0}
                >
                  Add {selectedFeatureTags.length} Feature{selectedFeatureTags.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Tag Detail Modal */}
      {selectedFeatureTag && (
        <FeatureTagDetailModal
          tag={selectedFeatureTag}
          isOpen={!!selectedFeatureTag}
          onClose={() => setSelectedFeatureTag(null)}
          tasks={tasks.filter(t => t.feature_tag?.id === selectedFeatureTag?.id)}
          users={[]}
          onTagUpdate={() => loadData()}
        />
      )}
    </div>
  );
};

export default ReleaseDetail;


